import React, { useCallback, useEffect } from 'react';
import { InvoiceForClientFragment, StripePaymentIntentStatusFragment } from 'shared/types/graphql';
import { getInvoiceIdentifierCopy, getMonetaryDisplayValue, getUserTimezoneAbbreviation } from 'shared/utils';
import format from 'date-fns/format';
import { HStack, VStack, Box, Flex, Skeleton } from '@televet/kibble-ui/build/chakra';
import { Tag } from '@televet/kibble-ui/build/components';

interface IInvoiceDisplayProps {
  invoice?: InvoiceForClientFragment;
  isPendingPaymentIntents: (intents: StripePaymentIntentStatusFragment[]) => boolean;
  setSelectedInvoiceId: (invoiceId: string | null) => void;
  selectedInvoiceId: string | null;
  updateInvoiceCost?: (invoiceTotal: number) => void;
  setIsBalanceSelected: (isBalanceSelected: boolean) => void;
  setValidationMessage: React.Dispatch<React.SetStateAction<string>>;
  showSkeleton?: boolean;
}

const InvoiceDisplay = ({
  invoice,
  isPendingPaymentIntents,
  setSelectedInvoiceId,
  selectedInvoiceId,
  updateInvoiceCost,
  setIsBalanceSelected,
  setValidationMessage,
  showSkeleton,
}: IInvoiceDisplayProps): JSX.Element => {
  useEffect(() => {
    if (selectedInvoiceId === invoice?.id) {
      if (invoice.total && invoice.total < 0) {
        setSelectedInvoiceId(null);
        setValidationMessage('Cannot collect negative payment amount');
      }
      if (invoice.total && invoice.total > 0) {
        setValidationMessage('');
      }
    }
  }, [invoice?.id, invoice?.total, selectedInvoiceId, setSelectedInvoiceId, setValidationMessage]);

  const handleInvoiceClick = useCallback(() => {
    if (!invoice?.id) return;

    if (selectedInvoiceId === invoice.id) {
      setSelectedInvoiceId(null);
      if (!!updateInvoiceCost) {
        updateInvoiceCost(0);
      }
    } else if (!!selectedInvoiceId && selectedInvoiceId.length > 0 && selectedInvoiceId !== invoice.id) {
      return;
    } else {
      setSelectedInvoiceId(invoice?.id);
      if (!!updateInvoiceCost) {
        updateInvoiceCost(invoice.total / 100);
      }
    }
    setIsBalanceSelected(false);
  }, [selectedInvoiceId, invoice, setSelectedInvoiceId, updateInvoiceCost, setIsBalanceSelected]);

  return (
    <Flex
      fontSize="sm"
      fontWeight="bold"
      alignItems="center"
      my={2}
      mr={2}
      justifyContent="space-between"
      p={2}
      borderRadius="md"
      borderWidth={1.5}
      cursor="pointer"
      borderColor={selectedInvoiceId === invoice?.id ? 'border.focus' : 'border.default'}
      onClick={handleInvoiceClick}
    >
      <VStack alignItems="flex-start" spacing={2}>
        <Flex direction="column" gap={showSkeleton ? 1 : 0}>
          <HStack>
            <Skeleton isLoaded={!showSkeleton}>
              <Box fontSize="sm" maxWidth="140px">
                {`Invoice ${getInvoiceIdentifierCopy(invoice?.identifier, invoice?.date)}`}
              </Box>
            </Skeleton>
            {invoice?.stripePaymentIntents &&
              isPendingPaymentIntents(invoice.stripePaymentIntents) &&
              !showSkeleton && (
                <Tag
                  size="sm"
                  label="Pending"
                  backgroundColor="background.warning"
                  color="text.warning"
                  mt={1}
                  mr={2}
                />
              )}
          </HStack>
          <Skeleton isLoaded={!showSkeleton}>
            <Box fontSize="xs" fontWeight="normal" color="text.default">
              {`Updated: ${format(
                new Date(invoice?.date || null),
                `MM/dd/yy '·' h:mmaaaaa'm' '${getUserTimezoneAbbreviation()}'`,
              )}`}
            </Box>
          </Skeleton>
        </Flex>
      </VStack>
      <Skeleton isLoaded={!showSkeleton}>
        <Flex fontSize="lg" fontWeight="bold" mr={2}>
          {getMonetaryDisplayValue(invoice?.total, true)}
        </Flex>
      </Skeleton>
    </Flex>
  );
};

export default InvoiceDisplay;
